import { API_BASE_URL, apiClient } from './apiClient';
import { getPlatformMfaToken, isOwnApiOrigin, MFA_HEADER, MFA_VERIFIED_EVENT, setPlatformMfaToken } from './platformMfaToken';

/** Contrato Backend feat/platform-staff-2fa 4baf2ca8: /api/platform/mfa (Bearer; X-MFA-Token lo pone apiClient). */
const BASE = '/api/platform/mfa';

export type MfaStatus = {
  enrolled: boolean;
  mfa_valid: boolean;
  mfa_expires_at: string | null;
  recovery_codes_remaining: number | null;
  enrollment_pending: boolean;
};
export type EnrollStart = { otpauth_uri: string; expires_at: string };
export type EnrollConfirm = { enabled: boolean; recovery_codes: string[]; mfa_token: string; mfa_expires_at: string };
export type MfaToken = { mfa_token: string; mfa_expires_at: string };
export type RecoveryOut = MfaToken & { recovery_codes_remaining: number };

export const RECOVERY_CODE_RE = /^[A-Z0-9]{5}-[A-Z0-9]{5}-[A-Z0-9]{5}-[A-Z0-9]{5}$/;
/** Normaliza lo tecleado: mayusculas, sin espacios, guiones cada 5. */
export function formatRecoveryCode(raw: string): string {
  const s = raw.toUpperCase().replace(/[^A-Z0-9]/g, '').slice(0, 20);
  return (s.match(/.{1,5}/g) ?? []).join('-');
}
export const TOTP_RE = /^\d{6}$/;

/** Con limite de 15 s: el Gate no se queda en "Comprobando" si el servidor no contesta (pasa a error + Reintentar). */
export const MFA_STATUS_TIMEOUT_MS = 15_000;
export const getMfaStatus = async (signal?: AbortSignal): Promise<MfaStatus> =>
  (await apiClient.get<MfaStatus>(`${BASE}/status`, { timeout: MFA_STATUS_TIMEOUT_MS, signal })).data;
export const startEnrollment = async (): Promise<EnrollStart> => (await apiClient.post<EnrollStart>(`${BASE}/enroll/start`)).data;
export async function confirmEnrollment(code: string): Promise<EnrollConfirm> {
  const d = (await apiClient.post<EnrollConfirm>(`${BASE}/enroll/confirm`, { code })).data;
  setPlatformMfaToken(d.mfa_token, d.mfa_expires_at);
  return d;
}
export async function verifyMfa(code: string): Promise<MfaToken> {
  const d = (await apiClient.post<MfaToken>(`${BASE}/verify`, { code })).data;
  setPlatformMfaToken(d.mfa_token, d.mfa_expires_at);
  return d;
}
export async function redeemRecoveryCode(code: string): Promise<RecoveryOut> {
  const d = (await apiClient.post<RecoveryOut>(`${BASE}/recovery`, { code: formatRecoveryCode(code) })).data;
  setPlatformMfaToken(d.mfa_token, d.mfa_expires_at);
  return d;
}
/**
 * Opcion B (acotada): tras verificar, UNA sola llamada a /api/auth/me con X-MFA-Token explicito, para que /me publique
 * is_platform_staff=true. No se agrega /api/auth/me a MFA_PATH_RULES: el token no viaja en ningun otro /me.
 * Avisa con MFA_VERIFIED_EVENT ({ user }); AuthContext lo aplica. Sin token => no hace nada. Nunca lanza.
 */
const ME_PATH = '/api/auth/me';
export async function refreshMeWithMfa(): Promise<void> {
  const token = getPlatformMfaToken();
  if (!token) return;
  // El header va explicito (no pasa por needsMfaHeader): solo si /me resuelve al origen propio (pagina o API_BASE_URL).
  if (!isOwnApiOrigin(ME_PATH, apiClient.defaults.baseURL, API_BASE_URL)) return;
  try {
    const r = await apiClient.get(ME_PATH, { headers: { [MFA_HEADER]: token } });
    if (r?.data && typeof r.data === 'object') window.dispatchEvent(new CustomEvent(MFA_VERIFIED_EVENT, { detail: { user: r.data } }));
  } catch { /* se queda con el /me anterior; el acceso lo sigue mostrando pending */ }
}
/** Requiere X-MFA-Token vigente (apiClient) y un codigo TOTP en el body. */
export const regenerateRecoveryCodes = async (code: string): Promise<{ recovery_codes: string[] }> =>
  (await apiClient.post<{ recovery_codes: string[] }>(`${BASE}/recovery-codes/regenerate`, { code })).data;
